import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getConversationTeamShares, getTeamConversations, setConversationTeamShare } from './api';
import type { TeamConversationShare } from './types';

export const teamConversationKeys = {
  shares: (conversationId: string) => ['team-conversation-shares', conversationId] as const,
  team: (teamId: string) => ['team-conversations', teamId] as const
};

export function useConversationTeamShares(conversationId: string, enabled = true) {
  const { getAccessTokenSilently } = useAuth0();
  return useQuery({
    queryKey: teamConversationKeys.shares(conversationId),
    enabled: enabled && Boolean(conversationId),
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently();
      return getConversationTeamShares({ accessToken, conversationId });
    },
    refetchOnWindowFocus: false
  });
}

export function useTeamConversations(teamId: string, enabled = true) {
  const { getAccessTokenSilently } = useAuth0();
  return useQuery({
    queryKey: teamConversationKeys.team(teamId),
    enabled: enabled && Boolean(teamId),
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently();
      return getTeamConversations({ accessToken, teamId });
    }
  });
}

export function useSetConversationTeamShare() {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      conversationId,
      share,
      teamId
    }: {
      conversationId: string;
      share: boolean;
      teamId: string;
    }) => {
      const accessToken = await getAccessTokenSilently();
      return setConversationTeamShare({ accessToken, conversationId, share, teamId });
    },
    onSuccess: (result, { conversationId, share, teamId }) => {
      queryClient.setQueryData<TeamConversationShare[]>(
        teamConversationKeys.shares(conversationId),
        (current) => {
          if (!current) return current;
          if (!share) return current.filter((item) => item.team_id !== teamId);
          const shared = result as TeamConversationShare;
          return [...current.filter((item) => item.team_id !== teamId), shared];
        }
      );
      void queryClient.invalidateQueries({
        queryKey: teamConversationKeys.shares(conversationId)
      });
      void queryClient.invalidateQueries({ queryKey: teamConversationKeys.team(teamId) });
    }
  });
}
